/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import {
  KatSpinner, KatDivider, KatIcon, KatAlert, KatBreadcrumb, KatBreadcrumbItem, KatPopover,
} from '@amzn/katal-react';
import ReplyFilter from 'src/components/thread-detail/ReplyFilter';
import PostsContainer from 'src/components/thread-detail/PostsContainer';
import DiscussionPost from 'src/components/thread-detail/DiscussionPost';
import { Helmet } from 'react-helmet-async';
import {
  selectThreadDetailOp,
  selectThreadDetailFetchStatus,
  selectFilter,
  selectSort,
  threadDetailActions,
  selectDraftPost,
  fetchThread,
  fetchDraft,
  selectGetDraftStatus,
  CancelFetchThread,
  selectIsServerInitializedThread,
} from 'src/redux/slices/threadDetailSlice';
import {
  useTranslation, Metrics, isStatusLoading, useFirstRender, usePrevious,
} from 'src/utils';
import ReplyPostPanel from 'src/components/thread-detail/ReplyPostPanel';
import { useAppSelector, useAppDispatch } from 'src/redux/hooks';
import { fetchCategories } from 'src/redux/slices/threadCategories';
import FetchStatus from 'src/types/FetchStatus';
import { AppState } from 'src/redux/store';
import { getGroupListing, getThreadDetail, THREAD_LISTING } from 'src/constants/pages';
import { ProfileStatus } from 'src/types/UserInfo';
import { FilterOptions, SortOptions } from 'src/types/DiscussionPost';
import { useInView } from 'react-intersection-observer';
import SimilarDiscussionsContainer from 'src/components/similar-discussions/SimilarDiscussionsContainer';
import GroupBanner from 'src/components/groups/GroupBanner';
import { fetchGroups } from 'src/redux/slices/groups';
import QuotesWrapper from 'src/components/thread-detail/QuotesWrapper';
import { createEditor } from 'slate';
import withPlugin from 'src/components/RichTextEditor/withPlugin';
import { withHistory } from 'slate-history';
import { withReact } from 'slate-react';
import SolutionPostContainer from 'src/components/thread-detail/SolutionPostContainer';
import { updateMarketplaceAction, userInfoActions } from 'src/redux/slices/userInfo';
import { setShowMarketplaceBanner } from 'src/redux/slices/appNotifications';
import { MethodName, Pages } from 'src/constants/metrics';
import PrivateGroupThreadWarning from 'src/components/thread-detail/PrivateGroupThreadWarning';
import ThreadNotFound from 'src/components/thread-detail/ThreadNotFound';
import type { ThreadDetailPageResponse } from 'src/server/components/thread-detail/thread-detail-page-data';
import HttpStatusCode from 'src/constants/httpStatusCodes';
import WeblabId from 'src/weblab/weblabId';

import pageData from 'src/server/components/thread-detail/server-side-props';
import { selectIsWeblabIdInT1 } from 'src/redux/slices/weblab';

export const getServerSideProps = pageData;

export const BREAD_CRUMB_MAX_LENGTH = 50;

const ThreadDetailPage = (pageProps?: ThreadDetailPageResponse) => {
  const { t } = useTranslation();
  const { query, push } = useRouter();
  const dispatch = useAppDispatch();
  const prevRoute = useAppSelector(({ routeHistory }: AppState) => routeHistory.prevRoute);
  const threadFetchStatus = useAppSelector(selectThreadDetailFetchStatus);
  const hasNoAccessToPrivateDiscussion = useAppSelector((state: AppState) => state
    .threadDetail
    .hasNoAccessToPrivateDiscussion);
  const draftPost = useAppSelector(selectDraftPost);
  const sortBy = useAppSelector(selectSort);
  const filterBy = useAppSelector(selectFilter);
  const isServerInitializedThread = useAppSelector(selectIsServerInitializedThread);
  const categoriesStatus = useAppSelector((state: AppState) => state.threadCategories.status);
  const groupsStatus = useAppSelector(({ groups }) => groups.status);
  const { threadId, postId } = query as { threadId: string, postId: string };
  const opPost = useAppSelector(selectThreadDetailOp);
  const opPostGroup = useAppSelector((state) => (opPost?.groupId != null
    ? state.groups.groups[opPost?.groupId] : undefined));
  const getDraftStatus = useAppSelector(selectGetDraftStatus);
  const {
    profileStatus,
    hasWriteAccess,
    selectedMarketplaceId,
  } = useAppSelector(({ userInfo }: AppState) => userInfo);
  const customEditor = useMemo(
    () => withPlugin(withHistory(withReact(createEditor()))),
    [],
  );

  const prevQueryParams = usePrevious({ threadId, postId });
  const shouldSaveUnauthMp = useAppSelector((state: AppState) => selectIsWeblabIdInT1(
    state,
    WeblabId.SELLER_FORUMS_THREAD_DETAILS_SSR,
  ));

  const isPersistedPreference = profileStatus === ProfileStatus.registered || shouldSaveUnauthMp;

  /**
   * Force setting the Op post's marketplace in the mp picker
   */
  useEffect(() => {
    if (opPost && selectedMarketplaceId !== opPost.marketplaceId) {
      dispatch(userInfoActions.setPrevMarketplace(selectedMarketplaceId));
      updateMarketplaceAction({
        marketplaceId: opPost.marketplaceId,
        dispatch,
        isPersistedPreference,
      });
      dispatch(setShowMarketplaceBanner(true));
    }
  }, [opPost]);

  useEffect(() => {
    if (threadFetchStatus === FetchStatus.complete) {
      const pageMetrics = new Metrics(MethodName.pageLoad);
      pageMetrics.publishPageLoad(Pages.threadDetail);
    }
  }, [threadFetchStatus]);

  const firstRender = useFirstRender();

  /**
   * Used for client side rendering. Will be removed once SSR is fully dialed up
   */
  useEffect(() => {
    // reset state if thread changes
    if (opPost?.threadId !== threadId && !isServerInitializedThread) {
      dispatch(threadDetailActions.reset());
    }

    const fetchThreadRequestParams = postId ? {
      threadId,
      postId,
      sortBy: SortOptions.oldest,
      filterBy: FilterOptions.allReplies,
    } : {
      threadId,
      sortBy,
      filterBy,
    };

    const fetchThreadPromise = (!isServerInitializedThread)
      ? dispatch(fetchThread(fetchThreadRequestParams))
      : undefined;

    if (categoriesStatus !== FetchStatus.complete) dispatch(fetchCategories());
    if (groupsStatus !== FetchStatus.complete) dispatch(fetchGroups());
    return () => {
      if (fetchThreadPromise) {
        fetchThreadPromise.abort(CancelFetchThread);
      }
    };
  }, [threadId, postId, sortBy, filterBy]);

  /**
   * For server side rendering
   */
  useEffect(() => {
    const fetchThreadRequestParams = postId ? {
      threadId,
      postId,
      sortBy: SortOptions.oldest,
      filterBy: FilterOptions.allReplies,
    } : {
      threadId,
      sortBy,
      filterBy,
    };

    const fetchThreadPromise = (!firstRender
      && isServerInitializedThread
      && prevQueryParams?.threadId === threadId
    )
      ? dispatch(fetchThread(fetchThreadRequestParams))
      : undefined;

    return () => {
      if (fetchThreadPromise) {
        fetchThreadPromise.abort(CancelFetchThread);
      }
    };
  }, [sortBy, filterBy, postId]);

  useEffect(() => {
    if (
      profileStatus === ProfileStatus.registered
      && threadFetchStatus === FetchStatus.complete
      && getDraftStatus === FetchStatus.idle
      && !!hasWriteAccess
    ) {
      dispatch(fetchDraft({ threadId }));
    }
  }, [threadFetchStatus, getDraftStatus]);
  const { ref, inView } = useInView({ threshold: 0 });

  const scrollToTopHandler = () => {
    window.scrollTo({ top: 0 });
    push(
      getThreadDetail(threadId),
      undefined,
      {
        shallow: true,
      },
    );
  };

  // eslint-disable-next-line react/destructuring-assignment
  const statusCode = pageProps?.statusCode;
  if (statusCode && [HttpStatusCode.NOT_FOUND, HttpStatusCode.BAD_REQUEST].includes(statusCode)) {
    return <ThreadNotFound />;
  }
  if (statusCode === HttpStatusCode.FORBIDDEN) {
    return <PrivateGroupThreadWarning />;
  }

  if ((isStatusLoading(threadFetchStatus) && !opPost)
      || isStatusLoading(categoriesStatus, groupsStatus)) {
    return <KatSpinner size="large" />;
  }

  if (threadFetchStatus === FetchStatus.failed && !opPost) {
    if (hasNoAccessToPrivateDiscussion) {
      return <PrivateGroupThreadWarning />;
    }
    return (
      <KatAlert
        className="container mt-5"
        variant="warning"
        header={t('request_pageLoadFailureHeader')}
        description={t('reload_page')}
        persistent
      />
    );
  }

  function renderRightSideBar() {
    if (draftPost) {
      return (
        <ReplyPostPanel
          customEditor={customEditor}
          parentPostId={draftPost.parentPostId}
          type={draftPost.type}
          postId={draftPost.id}
          opPostGroup={opPostGroup}
        />
      );
    }

    return (
      <div className="mt-6.5 space-y-3.5">
        {
          selectedMarketplaceId === opPost.marketplaceId && (
          <SimilarDiscussionsContainer
            searchByContent={opPost.content}
            searchByTitle={opPost.title}
          />
          )
        }
      </div>
    );
  }

  const getThreadContent = () => (
    <QuotesWrapper key={opPost.id} customEditor={customEditor}>
      {(quoteRef) => (
        <>
          {
                opPost && (
                  <div ref={ref}>
                    <DiscussionPost
                      quoteRef={quoteRef}
                      postId={opPost.id}
                    />
                  </div>
                )
              }
          <SolutionPostContainer />
          <KatDivider variant="athens" className="bg-kat-seattle" />
          <ReplyFilter />
          <KatDivider variant="athens" className="bg-kat-seattle" />
          <PostsContainer quoteRef={quoteRef} threadId={threadId} />
        </>
      )}
    </QuotesWrapper>
  );

  if (opPost.isDeleted) {
    return <ThreadNotFound />;
  }

  return (
    <div data-testid="thread-detail-page" className="container  mb-4">
      <Helmet>
        <title>{opPost.title}</title>
      </Helmet>

      {opPost.groupId && <GroupBanner groupId={opPost.groupId} />}
      <div className="kat-row">
        <div className="kat-col-md-8 mt-6.5 ltr:pr-10 rtl:pl-10">
          {
            prevRoute && [THREAD_LISTING, getGroupListing(opPost.groupId as string)].some(
              (route) => prevRoute.startsWith(route),
            )
            && (
              <KatBreadcrumb className="my-1">
                <KatBreadcrumbItem
                  data-testid="previous-page-breadcrumb"
                  className="cursor-pointer"
                  onClick={() => push(prevRoute)}
                  label={t('threadDetail_discussionsBreadcrumb')}
                />
                <KatBreadcrumbItem
                  data-testid="current-page-breadcrumb"
                  /**
                   * @todo check if truncation is based on element width instead of characters
                   */
                  label={opPost.title.length > BREAD_CRUMB_MAX_LENGTH
                    ? `${opPost.title.substring(0, BREAD_CRUMB_MAX_LENGTH)}...` : opPost.title}
                />
              </KatBreadcrumb>
            )
          }
          {
            categoriesStatus === FetchStatus.failed
            && (
            <KatAlert
              data-testid="categories-request-failure"
              variant="warning"
              className="mb-1"
              persistent
              header={t('request_categoriesRequestFailureHeader')}
              description={t('request_categoriesRequestFailureDescription')}
            />
            )
          }
          {
            getDraftStatus === FetchStatus.failed
            && (
            <KatAlert
              data-testid="draft-request-failure"
              variant="warning"
              className="mb-1"
              persistent
              header={t('request_threadDraftRequestFailureHeader')}
              description={t('request_threadDraftRequestFailureDescription')}
            />
            )
          }
          {getThreadContent()}
          {
            !inView && (
            <div className="flex flex-row-reverse">
              <div className="fixed bottom-14 z-[101] flex w-36 flex-row-reverse">
                <KatPopover
                  position="top"
                  variant="tooltip"
                  triggerType="hover"
                >
                  <button
                    slot="trigger"
                    data-testid="scroll-top"
                    type="button"
                    className="flex h-[38px] w-[38px] cursor-pointer flex-col items-center justify-center rounded-full border-0 bg-kat-tarpon shadow-[0_2px_6px_0_rgba(0,0,0,0.25)]"
                    onClick={scrollToTopHandler}
                  >
                    <KatIcon className="text-kat-white" name="arrow-up" size="small" />
                  </button>
                  {t('threadDetail_scrollToTop')}
                </KatPopover>
              </div>
            </div>
            )
          }
        </div>
        <div className="kat-col-md-4 border border-y-0 border-solid border-kat-seattle bg-kat-snow px-7.5">
          { renderRightSideBar() }
        </div>
      </div>
    </div>
  );
};

ThreadDetailPage.defaultProps = {};

export default ThreadDetailPage;
